import {Button, Group, Paper, Stack, Text, Title} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { RichTextEditor } from '@mantine/tiptap';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TiptapLink from '@tiptap/extension-link';
import {showNotification} from '@mantine/notifications';
import {useEffect, useState} from 'react';

import { ToS } from 'src/types/models/ToS';
import useTranslation from 'src/i18next.config';

type ToSFormProps = {
    initialToS?: ToS;
    submitBtnLbl: string;
    title: string;
    onSave: (payload: ToS) => void;
};


export function ToSForm({initialToS, submitBtnLbl, title, onSave}: ToSFormProps) {
    const {t} = useTranslation();

    const [formCompleted, setFormCompleted] = useState(false);
    const [inputDate, setInputDate] = useState<Date>(new Date());

    const editor = useEditor({
        extensions: [
            StarterKit,
            TiptapLink,
        ],
        content: initialToS?.contents ?? '',
    });

    useEffect(() => {
        if (initialToS) {
            setInputDate(new Date(initialToS.intoEffectAt));
            if (editor && initialToS.contents) {
                editor.commands.setContent(initialToS.contents);
            }
        }
    }, [initialToS, editor]);

    useEffect(() => {
        const content = editor?.getHTML() ?? '';
        if (content.trim() !== '' && content !== '<p></p>') {
            setFormCompleted(true);
        } else {
            setFormCompleted(false);
        }
    }, [inputDate, editor?.state]);

    async function saveClicked(values: ToS) {
        if (!formCompleted) {
            const content = editor?.getHTML() ?? '';
            if (content.trim() === '' || content === '<p></p>') {
                alert("ToS contents cannot be empty");
            }
            showNotification({
                title: t('formNotCompleted'),
                message: t('formNotCompletedMessage'),
                color: 'red',
            });
        } else {
            onSave(values);
        }
    }

    return (
        <Paper
            p="md"
            shadow="md"
            component="form"
            noValidate
            style={{
                width: '100%',
                maxWidth: 900,
                margin: 'auto',
                padding: 24,
            }}
        >
            <Stack gap="lg">
                <Title order={2}>{title}</Title>
                <Group
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                    }}
                >
                    <Text
                        size="lg"
                        fw={500}
                        td="underline"
                    >Terms of Service:</Text>
                    <RichTextEditor editor={editor} style={{ minHeight: 400 }}>
                        <RichTextEditor.Toolbar sticky stickyOffset={60}>
                            <RichTextEditor.ControlsGroup>
                                <RichTextEditor.Bold />
                                <RichTextEditor.Italic />
                                <RichTextEditor.Underline />
                                <RichTextEditor.Strikethrough />
                                <RichTextEditor.ClearFormatting />
                            </RichTextEditor.ControlsGroup>

                            <RichTextEditor.ControlsGroup>
                                <RichTextEditor.H1 />
                                <RichTextEditor.H2 />
                                <RichTextEditor.H3 />
                                <RichTextEditor.H4 />
                            </RichTextEditor.ControlsGroup>

                            <RichTextEditor.ControlsGroup>
                                <RichTextEditor.BulletList />
                                <RichTextEditor.OrderedList />
                            </RichTextEditor.ControlsGroup>

                            <RichTextEditor.ControlsGroup>
                                <RichTextEditor.Link />
                                <RichTextEditor.Unlink />
                            </RichTextEditor.ControlsGroup>
                        </RichTextEditor.Toolbar>

                        <RichTextEditor.Content />
                    </RichTextEditor>
                </Group>
                <Group
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                    }}
                >
                    <Text
                        size="lg"
                        fw={500}
                        td="underline"
                    >Enters into effect starting:</Text>
                    <DatePickerInput
                        value={inputDate}
                        onChange={(val) => setInputDate(val ?? new Date())}
                    />
                </Group>
                <div style={{marginTop: 10, display: 'flex', columnGap: '1rem'}}>
                    <Button color="cyan.8" onClick={() => saveClicked({
                        approved: false,
                        _id: initialToS?._id || '',
                        contents: editor?.getHTML() || '',
                        intoEffectAt: inputDate
                    })}>
                        {t(`${submitBtnLbl}`)}
                    </Button>
                </div>
            </Stack>
        </Paper>
    )
}
